// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import i18next from 'i18next';
import {
  gameKeysWorkflow,
  exportGameKeysData,
} from '../../../actions/metricActions';
import { NavLink, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import KeyProviderLink from '../../common/links/KeyProviderLink';
import PlatformLink from '../../common/links/PlatformLink';
import skuHelp from '../../../constants/help/skuHelp';
import { scopes as tableScopes } from '../../../constants/scopes';
import { useTranslation } from 'react-i18next';
import WorkflowLink from '../../common/links/WorkflowLink';

export interface GameKeysListByWorkflowProps {
  reload?: any;
  setBreadcrumb: (crumb: any) => void;
  tableViewSwitcher?: React.ReactNode;
}

function GameKeysListByWorkflow({ reload, setBreadcrumb, tableViewSwitcher }: GameKeysListByWorkflowProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const language = i18next.language;
  const allColumns = [
    'gameName',
    'platformName',
    'keyProviderName',
    'keyWorkflowName',
    'isAvailableCodeCount',
    'isAllocatedCodeCount',
    'isSoldCodeCount',
    'isActivatedCodeCount',
    'isDisabledCodeCount',
    'isExpiredCodeCount',
  ];
  const defaultVisibleColumns = allColumns;

  const [gamesKeysWorkflow, setGamesKeysWorkflow] = useState<any>();
  const params = new URLSearchParams(location.search);

  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'gamesKeysWorkflow-settings',
      getTableSettingsDefaults({
        defaultSort: 'gameName',
        defaultVisibleColumns,
      }),
      null,
      gameKeysWorkflow,
      setGamesKeysWorkflow,
      reload,
      t('Failed to load game keys')
    );

  let columnsConfig = [
    {
      Key: 'gameName',
      Label: t('Game'),
      Render: (item: any) => (
        <>
          <NavLink to={`/sku/${item.skuId}`} className="game-title">
            {item.gameName}
          </NavLink>
          <br />
          <NavLink to={`/sku/${item.skuId}`}>{item.skuName}</NavLink>
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'platformName',
      Label: t('Platform'),
      Render: (item: any) => (
        <PlatformLink id={item.platformId} name={item.platformName} />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyProviderName',
      Label: t('Key provider'),
      Render: (item: any) => (
        <KeyProviderLink id={item.keyProviderId} name={item.keyProviderName} />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyWorkflowName',
      Label: t('Pool'),
      Render: (item: any) => (
        <WorkflowLink
          id={item.keyWorkflowId}
          key={item.keyWorkflowId}
          name={item.keyWorkflowName}
          colour={item.keyWorkflowColourHex}
        />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'isAvailableCodeCount',
      Label: t('Pooled'),
      helpContent: skuHelp.SKU_POOL_TABLE_POOLED[language],
      className: 'text-right num no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.isAvailableCodeCount),
    },
    {
      Key: 'isAllocatedCodeCount',
      Label: t('Delivered'),
      className: 'text-right num no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.isAllocatedCodeCount),
    },
    {
      Key: 'isSoldCodeCount',
      Label: t('Reported sold'),
      className: 'text-right num no-wrap',
      Render: (item: any) =>
        item.isSoldCodeCount > 0 ? (
          <NavLink to={`/keys/reported-sold/?${tableScopes.SKU}=${item.skuId}`}>
            {CommaNumber_NoDecimal(item.isSoldCodeCount)}
          </NavLink>
        ) : (
          0
        ),
    },
    {
      Key: 'isActivatedCodeCount',
      Label: t('Activated'),
      className: 'text-right num no-wrap',
      Render: (item: any) =>
        item.isActivatedCodeCount > 0 ? (
          <NavLink
            to={`/keys/reported-activated/?${tableScopes.SKU}=${item.skuId}`}
          >
            {CommaNumber_NoDecimal(item.isActivatedCodeCount)}
          </NavLink>
        ) : (
          0
        ),
    },
    {
      Key: 'isDisabledCodeCount',
      Label: t('Disabled'),
      className: 'text-right num no-wrap',
      Render: (item: any) =>
        item.isDisabledCodeCount > 0 ? (
          <NavLink
            to={`/keys/reported-disabled/?${tableScopes.SKU}=${item.skuId}`}
          >
            {CommaNumber_NoDecimal(item.isDisabledCodeCount)}
          </NavLink>
        ) : (
          0
        ),
    },
    {
      Key: 'isExpiredCodeCount',
      Label: t('Expired'),
      className: 'text-right num no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.isExpiredCodeCount),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return gamesKeysWorkflow ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        allColumns={allColumns}
        rows={gamesKeysWorkflow.data.map((x: any) => ({ Key: x.skuId, ...x }))}
        totalRowCount={gamesKeysWorkflow.totalRowCount}
        filteredRowCount={gamesKeysWorkflow.filteredRowCount}
        scopes={gamesKeysWorkflow.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportGameKeysData}
        setBreadcrumb={setBreadcrumb}
        tableActionBarButtons={tableViewSwitcher}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default GameKeysListByWorkflow;
